import { useState, useRef, useEffect } from "react";
import type { User } from "@supabase/supabase-js";
import type { Tenant } from "@/lib/types";
import { Card, CardContent } from "@/components/ui/card";
import { cardRequestService } from "@/services";
import { Wallet, Lock, ArrowDownLeft, ArrowUpRight, RefreshCw, BarChart3, CreditCard } from "lucide-react";

interface WalletData {
  id: string;
  label: string;
  currency: string;
  symbol: string;
  balance: number;
  active: boolean;
}

interface Transaction {
  id: string;
  description: string;
  date: string;
  amount: number;
  currency: string;
  type: "credit" | "debit";
}

interface Props {
  tenant: Tenant;
  user: User;
}

const MOCK_TRANSACTIONS: Transaction[] = [];

const UserCard = ({ tenant, user }: Props) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [cardRequest, setCardRequest] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const transactions = MOCK_TRANSACTIONS;

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const cr = await cardRequestService.getUserLatestCardRequest(user.id, tenant.id);
      setCardRequest(cr.data);
      setLoading(false);
    };
    load();
  }, [user.id, tenant.id]);

  const isWaitlist = tenant.tenantType === "waitlist";

  // Use actual funding amount from card request if available, otherwise fall back to tenant default
  const actualFundingCents = cardRequest?.funding_amount_cents || null;
  const defaultFundingCents = (tenant.fundingAmountsCents || [2500])[0] || 2500;
  const displayFundingCents = actualFundingCents || defaultFundingCents;

  const wallets: WalletData[] = isWaitlist
    ? [
        { id: "usd", label: "Personal", currency: "USD", symbol: "$", balance: displayFundingCents / 100, active: false },
        { id: "rewards", label: "Rewards", currency: "USD", symbol: "$", balance: 0, active: false },
      ]
    : [
        { id: "usd", label: "Personal", currency: "USD", symbol: "$", balance: 0, active: false },
      ];

  const handleScroll = () => {
    if (!scrollRef.current) return;
    const el = scrollRef.current;
    const cardWidth = el.scrollWidth / wallets.length;
    const idx = Math.round(el.scrollLeft / cardWidth);
    setActiveIndex(idx);
  };

  const currentWallet = wallets[activeIndex];

  return (
    <div className="max-w-lg mx-auto animate-fade-in">

      {/* Wallet Hero */}
      <div
        className="px-4 pt-4 pb-6"
        style={{ background: `linear-gradient(180deg, hsl(${tenant.primaryColor} / 0.08) 0%, transparent 100%)` }}
      >
        {/* Carousel */}
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="flex snap-x snap-mandatory overflow-x-auto scrollbar-hide gap-0"
          style={{ scrollbarWidth: "none" }}
        >
          {wallets.map((w, i) => (
            <div
              key={w.id}
              className="snap-center shrink-0 w-full flex flex-col items-center justify-center text-center px-4"
            >
              <p className="text-sm text-muted-foreground font-medium">
                {w.label} · {w.currency}
              </p>
              <p className={`text-4xl font-bold font-display tracking-tight mt-1 ${!w.active ? "text-muted-foreground/30" : "text-foreground"}`}>
                {w.symbol}{w.balance.toFixed(2)}
              </p>
              {!w.active && (
                <p className="text-xs text-muted-foreground mt-1">Activates when card is approved</p>
              )}
            </div>
          ))}
        </div>

        {/* Dots */}
        {wallets.length > 1 && (
          <div className="flex items-center justify-center gap-1.5 mt-4">
            {wallets.map((_, i) => (
              <div
                key={i}
                className="rounded-full transition-all duration-200"
                style={{
                  width: i === activeIndex ? 20 : 6,
                  height: 6,
                  backgroundColor: i === activeIndex
                    ? `hsl(${tenant.primaryColor})`
                    : `hsl(${tenant.primaryColor} / 0.2)`,
                }}
              />
            ))}
          </div>
        )}

        {/* Action pills */}
        <div className="flex items-center justify-center gap-6 mt-6">
          {[
            { icon: ArrowDownLeft, label: "Add Money" },
            { icon: RefreshCw, label: "Exchange" },
            { icon: BarChart3, label: "Analytics" },
            { icon: CreditCard, label: "Manage Card" },
          ].map(({ icon: Icon, label }) => (
            <button
              key={label}
              disabled
              className="flex flex-col items-center gap-1 opacity-40 cursor-not-allowed"
            >
              <div className="h-11 w-11 rounded-full bg-muted flex items-center justify-center">
                <Icon className="h-5 w-5 text-muted-foreground" />
              </div>
              <span className="text-[11px] text-muted-foreground">{label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Funding Details */}
      {cardRequest && (
        <div className="px-4 pt-2">
          <Card className="shadow-sm">
            <CardContent className="p-4 space-y-2 text-sm">
              {isWaitlist ? (
                <>
                  <h3 className="font-semibold text-sm mb-2">Card Funding Details</h3>
                   <div className="flex justify-between">
                     <span className="text-muted-foreground">Card funding</span>
                     <span className="font-medium">${(displayFundingCents / 100).toFixed(2)}</span>
                   </div>
                   {/* NO FALLBACK PRICE: unconfigured fee says so rather than inventing a figure. */}
                   {tenant.cardFeeCents === undefined ? (
                     <div className="flex justify-between">
                       <span className="text-muted-foreground">Card issuance fee</span>
                       <span className="font-medium text-destructive">Not available</span>
                     </div>
                   ) : (
                     <>
                       {tenant.cardFeeCents > 0 && (
                         <div className="flex justify-between">
                           <span className="text-muted-foreground">Card issuance fee</span>
                           <span className="font-medium">${(tenant.cardFeeCents / 100).toFixed(2)}</span>
                         </div>
                       )}
                       <div className="border-t pt-2 flex justify-between font-semibold">
                         <span>Total</span>
                         <span>${((tenant.cardFeeCents + displayFundingCents) / 100).toFixed(2)}</span>
                       </div>
                     </>
                   )}
                  <p className="text-xs text-muted-foreground pt-1">
                    {cardRequest.status === "waitlisted" ? "Charged when your card is approved" : `Status: ${cardRequest.status}`}
                  </p>
                </>
              ) : (
                <>
                  <h3 className="font-semibold text-sm mb-2">Card Request Details</h3>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Name</span>
                    <span className="font-medium">{cardRequest.first_name} {cardRequest.last_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Delivery</span>
                    <span className="font-medium capitalize">{cardRequest.delivery_type === "branch" ? "Branch Pickup" : "Post"}</span>
                  </div>
                  <div className="border-t pt-2">
                    <p className="text-xs text-muted-foreground">
                      Status: <span className="capitalize font-medium text-foreground">{cardRequest.status}</span>
                    </p>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Transactions */}
      <div className="px-4 pt-4 pb-24">
        <h3 className="text-sm font-semibold text-muted-foreground mb-3">Recent Transactions</h3>
        {transactions.length === 0 ? (
          <Card className="shadow-sm">
            <CardContent className="p-8 text-center">
              <Wallet className="h-8 w-8 mx-auto text-muted-foreground/30 mb-2" />
              <p className="text-sm text-muted-foreground">No transactions yet</p>
              <p className="text-xs text-muted-foreground/60 mt-1">
                Transactions will appear here once your account is active
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-1">
            {transactions.map((tx) => (
              <div
                key={tx.id}
                className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-muted/50 transition-colors"
              >
                <div className={`h-9 w-9 rounded-full flex items-center justify-center ${
                  tx.type === "credit" ? "bg-success/10" : "bg-destructive/10"
                }`}>
                  {tx.type === "credit" ? (
                    <ArrowDownLeft className="h-4 w-4 text-success" />
                  ) : (
                    <ArrowUpRight className="h-4 w-4 text-destructive" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{tx.description}</p>
                  <p className="text-xs text-muted-foreground">{tx.date}</p>
                </div>
                <p className={`text-sm font-semibold tabular-nums ${
                  tx.type === "credit" ? "text-success" : "text-foreground"
                }`}>
                  {tx.type === "credit" ? "+" : "-"}{tx.currency} {Math.abs(tx.amount).toFixed(2)}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default UserCard;
